import { type FC, useMemo } from 'react';

import { mergeSx } from '../../util/mui/sx';
import { Stack, type StackProps } from '../structural/Stack';

import { type ActionsProp } from './types';
import { pruneActions } from './util';

/**
 * Props for the {@link Actions} component that are applicable when the actions in the
 * {@link Actions} component are not being dynamically rendered based on non-JSX data structures.
 *
 * In this case, the actions can be provided as either an explicit prop name or via the 'children'
 * prop.
 */
export interface ExplicitActionsProps extends Omit<StackProps, 'children' | 'direction'> {
  readonly actions?: ActionsProp;
  readonly children?: ActionsProp;
}

export const ExplicitActions: FC<ExplicitActionsProps> = ({ actions, children, ...props }) => {
  const visibleActions = useMemo(() => pruneActions(actions ?? children), [children, actions]);
  if (visibleActions.length !== 0) {
    return (
      <Stack
        alignItems='center'
        data-testid='actions'
        gap={1}
        height='100%'
        maxHeight='100%'
        minHeight='100%'
        minWidth='fit-content'
        {...props}
        direction='row'
        sx={mergeSx(props.sx, {
          '& > *': {
            maxHeight: '100%',
          },
        })}
      >
        {visibleActions}
      </Stack>
    );
  }
  return null;
};
